import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { downloadFile, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import ConfirmDelete from "@/components/ConfirmDelete";
import { toast } from "sonner";
import { HardDriveDownload, Cloud, CloudOff, RefreshCw, Download, Upload, CheckCircle2, AlertTriangle, Clock, RotateCcw } from "lucide-react";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("it-IT"); } catch { return iso; } };

export default function Impostazioni() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [folderName, setFolderName] = useState("Backup Contabilità");
  const [autoEnabled, setAutoEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [driveFiles, setDriveFiles] = useState([]);
  const restoreRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(() => {
    setLoading(true);
    api.get("/backup/status").then((r) => {
      setStatus(r.data);
      setFolderName(r.data.settings?.folder_name || "Backup Contabilità");
      setAutoEnabled(r.data.settings?.auto_enabled !== false);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (searchParams.get("drive_connected")) { toast.success("Google Drive collegato"); setSearchParams({}); load(); }
    if (searchParams.get("drive_error")) { toast.error("Connessione a Google Drive non riuscita"); setSearchParams({}); }
  }, [searchParams, setSearchParams, load]);

  const connect = async () => {
    try {
      const { data } = await api.get("/oauth/drive/connect", { params: { origin: window.location.origin } });
      window.location.href = data.authorization_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Impossibile avviare la connessione"); }
  };

  const disconnect = async () => {
    await api.post("/backup/disconnect");
    toast.success("Google Drive scollegato");
    load();
  };

  const saveSettings = async () => {
    try {
      await api.post("/backup/settings", { folder_name: folderName, auto_enabled: autoEnabled });
      toast.success("Impostazioni salvate");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const runBackup = async () => {
    setBusy(true);
    try {
      await api.post("/backup/run");
      toast.success("Backup eseguito su Google Drive");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Backup fallito"); }
    finally { setBusy(false); }
  };

  const downloadBackup = async () => {
    setBusy(true);
    try {
      await downloadFile("/backup/download", `backup_${Date.now()}.zip`);
      toast.success("Backup scaricato");
    } catch { toast.error("Download non riuscito"); }
    finally { setBusy(false); }
  };

  const onRestoreFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/backup/restore", fd);
      const tot = Object.values(data.counts || {}).reduce((s, n) => s + n, 0);
      toast.success(`Ripristino completato (merge): ${tot} record`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ripristino non riuscito"); }
    finally { setBusy(false); }
  };

  const loadDriveFiles = async () => {
    try {
      const { data } = await api.get("/backup/drive-files");
      setDriveFiles(data.files || []);
      if ((data.files || []).length === 0) toast.info("Nessun backup trovato nella cartella Drive");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const restoreDrive = async (fileId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/backup/restore-drive", { file_id: fileId });
      const tot = Object.values(data.counts || {}).reduce((s, n) => s + n, 0);
      toast.success(`Ripristino da Drive completato (merge): ${tot} record`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ripristino non riuscito"); }
    finally { setBusy(false); }
  };

  const connected = status?.drive_connected;
  const configured = status?.drive_configured;

  return (
    <div className="flex-1 overflow-auto px-6 py-6 lg:px-8 space-y-6">
      <PageHeader title="Impostazioni" subtitle="Backup automatico su Google Drive e ripristino" />

      <Card className="rounded-md border-slate-200 shadow-sm p-6 space-y-5" data-testid="backup-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Cloud className="h-5 w-5 text-blue-700" /> Backup su Google Drive</h3>
            <p className="text-sm text-slate-500 mt-1">I backup (JSON, Excel, BSON) vengono caricati ogni giorno alle 02:00 nella cartella scelta. Puoi eseguirli anche manualmente.</p>
          </div>
          {loading ? null : connected ? (
            <span data-testid="drive-connected-badge" className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Collegato</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-500"><CloudOff className="h-3.5 w-3.5" /> Non collegato</span>
          )}
        </div>

        {!configured && (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="drive-not-configured">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            Google Drive non è ancora configurato sul server (mancano CLIENT_ID/SECRET OAuth). Fornisci le credenziali per abilitare la connessione.
          </div>
        )}

        {connected ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-slate-600">Account: <strong>{status?.drive_email || "—"}</strong></span>
            <ConfirmDelete title="Scollegare Google Drive?" description="I backup automatici verranno interrotti." actionTestid="confirm-disconnect-drive" onConfirm={disconnect}>
              <Button variant="outline" size="sm" data-testid="disconnect-drive-button"><CloudOff className="h-4 w-4 mr-1.5" /> Scollega</Button>
            </ConfirmDelete>
          </div>
        ) : (
          <Button data-testid="connect-drive-button" onClick={connect} disabled={!configured} className="bg-blue-700 hover:bg-blue-800">
            <Cloud className="h-4 w-4 mr-1.5" /> Connetti Google Drive
          </Button>
        )}

        {connected && (
          <div className="grid gap-4 sm:grid-cols-2 pt-2 border-t border-slate-100">
            <div>
              <Label className="text-slate-700 text-sm">Cartella di destinazione su Drive</Label>
              <Input data-testid="folder-name-input" value={folderName} onChange={(e) => setFolderName(e.target.value)} className="mt-1.5 bg-white" placeholder="Backup/Contabilità" />
              <p className="text-xs text-slate-400 mt-1">Usa <code>/</code> per le sottocartelle (es. <code>Backup/Contabilità/2026</code>). Verranno create automaticamente se non esistono.</p>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
              <div>
                <div className="text-sm font-medium text-slate-800">Backup automatico giornaliero</div>
                <div className="text-xs text-slate-400">Ogni giorno alle 02:00</div>
              </div>
              <Switch data-testid="auto-backup-switch" checked={autoEnabled} onCheckedChange={setAutoEnabled} />
            </div>
            <div className="sm:col-span-2">
              <Button size="sm" data-testid="save-settings-button" onClick={saveSettings} variant="outline">Salva impostazioni</Button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
          {connected && (
            <Button data-testid="run-backup-button" onClick={runBackup} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">
              <HardDriveDownload className="h-4 w-4 mr-1.5" /> Esegui backup ora
            </Button>
          )}
          <Button variant="outline" data-testid="download-backup-button" onClick={downloadBackup} disabled={busy}>
            <Download className="h-4 w-4 mr-1.5" /> Scarica backup (ZIP)
          </Button>
        </div>
      </Card>

      <Card className="rounded-md border-slate-200 shadow-sm p-6 space-y-4" data-testid="restore-card">
        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><RotateCcw className="h-5 w-5 text-blue-700" /> Ripristino (merge)</h3>
        <p className="text-sm text-slate-500">Il ripristino unisce i dati del backup a quelli attuali (aggiorna/aggiunge, non cancella).</p>
        <div className="flex flex-wrap gap-2">
          <input ref={restoreRef} type="file" accept=".json" onChange={onRestoreFile} className="hidden" data-testid="restore-file-input" />
          <Button variant="outline" data-testid="restore-file-button" onClick={() => restoreRef.current?.click()} disabled={busy}>
            <Upload className="h-4 w-4 mr-1.5" /> Ripristina da file JSON
          </Button>
          {connected && (
            <Button variant="outline" data-testid="load-drive-files-button" onClick={loadDriveFiles} disabled={busy}>
              <RefreshCw className="h-4 w-4 mr-1.5" /> Backup su Drive
            </Button>
          )}
        </div>
        {driveFiles.length > 0 && (
          <div className="space-y-1.5" data-testid="drive-files-list">
            {driveFiles.map((f) => (
              <div key={f.id} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                <span className="text-sm text-slate-700 truncate">{f.name}</span>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs text-slate-400">{f.createdTime ? fmtDate(f.createdTime) : ""}</span>
                  <ConfirmDelete title="Ripristinare questo backup?" description="I dati verranno uniti a quelli attuali." actionTestid={`confirm-restore-${f.id}`} onConfirm={() => restoreDrive(f.id)} confirmLabel="Ripristina">
                    <Button variant="ghost" size="sm" data-testid={`restore-drive-${f.id}`} className="text-blue-700"><RotateCcw className="h-4 w-4 mr-1" /> Ripristina</Button>
                  </ConfirmDelete>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="rounded-md border-slate-200 shadow-sm p-6 space-y-3" data-testid="history-card">
        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Clock className="h-5 w-5 text-blue-700" /> Storico backup</h3>
        {(status?.history || []).length === 0 ? (
          <p className="text-sm text-slate-400">Nessun backup eseguito.</p>
        ) : (
          <div className="space-y-1.5">
            {status.history.map((h, i) => (
              <div key={i} data-testid={`history-row-${i}`} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2 text-sm">
                <div className="flex items-center gap-2">
                  {h.status === "ok" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-rose-600" />}
                  <span className="text-slate-700">{fmtDate(h.created_at)}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{h.mode}</span>
                </div>
                <span className={`text-xs font-medium ${h.status === "ok" ? "text-emerald-600" : "text-rose-600"}`}>
                  {h.status === "ok" ? `${(h.files || []).length} file` : (h.error || "errore")}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
